import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { parseISO } from 'date-fns';

import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * StoricoOrdiniComponent — lista degli ordini chiusi (storico).
 *
 * Filtri client-side combinati: search (n° ordine o cliente) + date
 * range (sulla data ordine) + customer select (estratto dalla risposta
 * server). Gli ordini parziali (`or_partial === 'S'`) sono evidenziati
 * con tint viola sulla riga + chip "Parziale" nel cliente.
 *
 * Sub-modale: `<app-storico-ordini-dettagli>` aperto via
 * `closeSrv.onStorico()` su row click o action click.
 *
 * Phase 1.5 / v2.9.0 — TS cleaned up (Chunk H.1):
 *   • dropped kendo-grid + 2× kendo-datepicker + 5× kendo-grid-column
 *     + kendoGridEditCommand + kendoGridCellTemplate (11 kendo refs
 *     → 0 in HTML),
 *   • dropped **8 dead calculate* methods** (`calculateTotalForJanuary`,
 *     `calculateTotalForMonth`, `calculateTotalForToday`,
 *     `calculateTotalForSpecificDay`, `calculateDailyGoal`,
 *     `calculateMonthlyGoal`, `calculatePreviousMonthDeficit`,
 *     `getWorkingDaysInMonth`) — il legacy aveva 4 commenti
 *     `// COMMENT THIS BEFORE DOING THE BUILD!!!! IT'S ONLY A TEST
 *     WITH DEV DATA` mai rispettati, e i metodi non sono più
 *     riferenziati dal template post-restyle (totali YTD vivono in
 *     Dashboard / Settings),
 *   • dropped `OrderTotalService` injection (era write-only via i
 *     calc methods dead; nessun lettore. Flagged task #188 Phase 2
 *     per eliminare anche il service),
 *   • dropped ~16 console.log/debug ("Gridus datas isust",
 *     "Order not found", "Daily objective", ecc.),
 *   • dropped 11 dead state fields: `orderImport`, `filteredOrder`,
 *     `searchString`, `searchDate`, `clientOrder`, `dateBy`,
 *     `searchService` (inject mai usato), `customerIndex`,
 *     `previousMonthDeficit`, totals, `pageSize`, `dates`, `customer`,
 *     `date`, `showAll2`, `netValue`, `isReady`, `isSearched`,
 *   • dropped 6 handler dead: `onMessengerNameSelect`, `onDateSelect`,
 *     `startSearch`, `removeData`, `onInputChanged`, `roWow` (kendo
 *     rowClass), `openLegenda`, `closeLegenda`, `toggleLegenda`,
 *   • dropped `setTimeout(100ms)` hack in `goToDetails` con flag
 *     `isReady` — workaround legacy non più necessario,
 *   • dropped accordion "Legenda Ordini Parziali" + `isLegendaOpen` +
 *     `legendaItems` — sostituiti da chip viola persistente
 *     nell'header + tint riga (pattern G.1 storico-parziali),
 *   • dropped pipes custom `byCustomerName` + `byDatePipe` +
 *     `byToClosingDate` — sostituiti da `applyClientFilters()`
 *     combinato (carrier + date range),
 *   • dropped `Subscription[]` + manual ngOnDestroy unsubscribe →
 *     `takeUntilDestroyed(destroyRef)`,
 *   • switch a `inject()` + `ChangeDetectorRef.detectChanges()`.
 *
 * Behaviour preserved 1:1: `getClosedOrders` con sort by closed_date
 * desc, `searchClosedOrders` con check `error_message.msg_code`,
 * `goToDetails → closeSrv.onStorico()`, query-param init flow.
 */
@Component({
  selector: 'app-storico-ordini',
  templateUrl: './storico-ordini.component.html',
  styleUrls: ['./storico-ordini.component.scss'],
  standalone: false,
})


export class StoricoOrdiniComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly orderSrv = inject(OrdersService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeSrv = inject(OpenModalService);

  // ── Data ──────────────────────────────────────────────────────────
  /** Server data unchanged; gridData is the post-filter view. */
  private originalData: Orders.IOrderClosed[] = [];
  public displayData: Orders.IOrderClosed[] = [];
  public customers: string[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public selectedCustomer: string | null = null;
  public clickedOrder = '';
  public readonly showAll = 'Mostra tutti';

  // ── Sort state ────────────────────────────────────────────────────
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // ── Date range filter (defaults: 2021-01-01 → today) ─────────────
  private readonly startDate = new Date(2021, 0, 1);
  /**
   * Fine range = "oggi" corrente.
   *
   * 🚨 FIX v2.14.2 (audit post-dashboard): pre-fix era
   * `readonly endDate = new Date()` come class-field init — pattern
   * che congela la data al momento della prima istanziazione del
   * componente. Se l'utente lascia aperta la pagina a cavallo di
   * mezzanotte, il filtro restava al giorno precedente escludendo
   * silenziosamente gli ordini di ieri.
   *
   * Ora è mutable e `refreshEndDate()` la riallinea in `ngOnInit`.
   */
  private endDate: Date = new Date();
  public filters: { startingDate: Date | null; endDate: Date } = {
    startingDate: this.startDate,
    endDate: this.endDate,
  };

  /** Riallinea endDate all'ora corrente. Vedi doc sopra. */
  private refreshEndDate(): void {
    this.endDate = new Date();
    this.filters.endDate = this.endDate;
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    // Sync endDate all'ora corrente (fix bug audit — vedi doc).
    this.refreshEndDate();
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.getOrders();
        } else {
          const searchParam: string = params['search'] ?? '';
          const dateParam: string | undefined = params['data'];
          this.filters.startingDate = dateParam
            ? parseISO(dateParam)
            : null;
          this.searchOrders(searchParam);
        }
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  getOrders(): void {
    this.loading = true;
    this.orderSrv
      .getClosedOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData) => {
          const list = ordersData ?? [];
          this.originalData = list;
          this.displayData = list;
          this.rebuildCustomers(list);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  searchOrders(search?: string): void {
    if (search !== undefined) this.filterTerm = search;

    // Empty term → reload full list (server-side search would return
    // 0 results for empty string per backend convention).
    if (!this.filterTerm || this.filterTerm.trim() === '') {
      this.getOrders();
      return;
    }

    this.loading = true;
    this.orderSrv
      .searchClosedOrders(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData: IWsResult) => {
          if (ordersData?.error_message?.msg_code !== 0) {
            this.originalData = [];
            this.displayData = [];
          } else {
            const list = ordersData.data ?? [];
            this.originalData = list;
            this.displayData = list;
            this.rebuildCustomers(list);
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private rebuildCustomers(list: Orders.IOrderClosed[]): void {
    const set = new Set(list.map((c) => c.or_customer ?? ''));
    this.customers = [this.showAll, ...Array.from(set)];
  }

  // ──────────────────────────────────────────────────────────────────
  // Filter handlers (combined client-side).
  // ──────────────────────────────────────────────────────────────────

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.startDate;
    this.filters.endDate = this.endDate;
    this.selectedCustomer = null;
    this.getOrders();
  }

  onCustomerChange(value: string | null): void {
    this.selectedCustomer =
      !value || value === this.showAll ? null : value;
    this.applyClientFilters();
  }

  onStartDateChange(value: Date | string | null): void {
    this.filters.startingDate = !value
      ? null
      : value instanceof Date
        ? value
        : new Date(value);
    this.applyClientFilters();
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
    this.applyClientFilters();
  }

  /** Combined client-side filter: customer + date range. */
  private applyClientFilters(): void {
    const cust = this.selectedCustomer;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;

    this.displayData = this.originalData.filter((item) => {
      if (cust && item.or_customer !== cust) return false;
      if (start && item.or_order_date) {
        const d = new Date(item.or_order_date);
        if (d < start) return false;
      }
      if (end && item.or_order_date) {
        const d = new Date(item.or_order_date);
        if (d > end) return false;
      }
      return true;
    });
    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────────
  // UI helpers.
  // ──────────────────────────────────────────────────────────────────

  /** True if the row corresponds to a partial-delivery order. */
  isPartial(item: Orders.IOrderClosed): boolean {
    return item.or_partial === 'S';
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail navigation.
  // ──────────────────────────────────────────────────────────────────

  goToDetails(order_id: string | number): void {
    this.clickedOrder = String(order_id);
    this.closeSrv.onStorico();
  }

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

}
